import { Button, Card, Form, Select, Skeleton, Spin } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getDefaultStr } from '../../../modules/crm/inPatient/service/crmUtils';
import { RootState } from '../../../store';
import { setApplicationDetailsRedux } from '../../../store/actions/applicationDetailAction';
import { getHospDetail, searchHospitals } from '../../../store/actions/authAction';
import { fetchHospitalLogo, fetchHospitalProfile, fetchHospitalProfileData } from '../../../store/actions/hospitalAction';
import { fetchProviderCredits } from '../../../store/actions/leftSideBarAction';
import { clearPayerLookUp } from '../../../store/actions/lookUpAction';
import { changePayer, setAllPayers } from '../../../store/actions/payerListAction';
import { ApplicationDetailModel } from '../../../store/models/applicationDetailModel';
import { centeredNotification, isPageNameProfile } from '../../../utils/utility';
import { APPID, NON_CONFIGURED_PAYERS_ERR, TYPEAHED_SEARCH_STRING_MIN_LENGTH } from '../../constants/commonConstants';
import useRefresh from '../../hooks/useRefresh';
import { Payer } from '../../models/payer.model';
import ApplicationDetail from '../../services/applicationDetail.service';
import {encryptText} from '../../../utils/encryptDecrypt';
import _ from 'lodash';

const ProviderSelectionComponent = ({ form, selectedHospital, setSelectedHospital, options, setOptions,
  onModalCancel }: any): ReactElement => {
  const dispatch = useDispatch();
  const [isSearching, setIsSearching] = useState(false);
  const [isHospLoading, setIsHospLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const { Option } = Select;
  const appDetail = useSelector((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const history = useHistory();
  const refresh = useRefresh(history, history.location.pathname);
  const appDetailService = ApplicationDetail.instance;

  useEffect((): any => {
    // disabling the button when the hospital is not selected
    if (!selectedHospital) {
      setDisabled(true);
    }
  }, [selectedHospital]);

    // calling HospitalDetals api after 1.5seconds the user stops typing
    const debouncedSearch = useCallback(
      _.debounce((event: string): any => {
        setOptions([]);
        if (event?.length >= TYPEAHED_SEARCH_STRING_MIN_LENGTH) {
          setIsSearching(true);
          dispatch(searchHospitals(event, onSearchSuccess, onSearchFailure));
        }
      }, 1500),
      [],
    );

    const onSearch = (event: any): any => {
      debouncedSearch(event);
    };

  const onChange = (hospId: any): any => {
    const selectedProvider = options.find(({ entityId }: any): boolean => entityId === hospId);
    setIsHospLoading(true);
    dispatch(getHospDetail([hospId], getHospSuccess, getHospFailure));
    setOptions([selectedProvider]);
  };

  const getHospSuccess = (result: any): any => {
    setIsHospLoading(false);
    setSelectedHospital(result);
    setDisabled(false);
  };

  const getHospFailure = (errMsg: string): any => {
    centeredNotification(errMsg);
    setIsHospLoading(false);
    setDisabled(true);
  };

  const onSearchSuccess = (result: any): any => {
    setIsSearching(false);
    setOptions(result);
  };

  const onSearchFailure = (): any => {
    setIsSearching(false);
  };

  const changeHospitalData = (): any => {
    localStorage.setItem(
      'providerDetail',
      encryptText(JSON.stringify(selectedHospital)),
    );
    // localStorage?.setItem('HospitalSeal', JSON.stringify(selectedHospital?.hospitalSeal));
    appDetailService.setApplicationDetail({
      userDetails: appDetail.getUserDetail(),
      providerDetail: selectedHospital,
      featureList: appDetailService.getFeatureList(),
    });
    dispatch(setApplicationDetailsRedux({ userDetail: appDetail.getUserDetail(), hospitalDetail: selectedHospital }));
    dispatch(fetchHospitalProfileData(null));
    onModalCancel();
    form.resetFields();
    setOptions([]);
    configurePayers();
    setSelectedHospital(null);
    dispatch(clearPayerLookUp());
    dispatch(fetchProviderCredits());
    refresh();
    if (!isPageNameProfile(history.location.pathname)) {
      dispatch(fetchHospitalProfile(null, onSuccess));
    }
  };

  const changeProvider = (): any => {
    form.validateFields().then((): any => {
      if (selectedHospital?.configuredPayers?.length === 0) {
        return centeredNotification(NON_CONFIGURED_PAYERS_ERR);
      }
      changeHospitalData();
    });
  };

  const configurePayers = (): any => {
    let { configuredPayers = [] } = selectedHospital;
    const { defaultConfiguredPayerId } = selectedHospital;
    configuredPayers = configuredPayers.map((payer: Payer): Payer => ({
      ...payer,
      value: payer.uniqueIdentifier,
      key: payer.uniqueIdentifier,
    }));
    const defaultPayer = configuredPayers?.find(({ uniqueIdentifier }: Payer): boolean => defaultConfiguredPayerId === uniqueIdentifier);

    dispatch(setAllPayers(configuredPayers));
    dispatch(changePayer(defaultPayer || (configuredPayers ? configuredPayers[0] : null)));
  };

  const onSuccess = ([{documentDetails}]: any): any => {
    const fileIdentifiers = documentDetails[0]?.docId ? [documentDetails[0]?.docId] : [];
    const request = {
      applicationId: APPID,
      fileIdentifiers,
    };
    dispatch(fetchHospitalLogo(request));
  };

  const onProceed = (): any => {
    // for routing back to inpatient page
    history.replace('/claims/in-patient/');
  };

  const onClickCancel = (): any => {
    setOptions([]);
    setSelectedHospital(null);
    onModalCancel(setSelectedHospital);
  };

  return (
    <React.Fragment>
      <Form layout="vertical" form={form} onFinish={changeProvider}>
        <Form.Item label="Select hospital" name="provider" rules={[{ required: true, message: 'Search Keyword is required' }]}>
          <Select
            data-testid="search-hospital"
            placeholder="Enter search keyword"
            onSearch={onSearch}
            showSearch={true}
            onChange={onChange}
            notFoundContent={isSearching ? <Spin size="small" /> : null}
            filterOption={(): boolean => true}
          >
            {
              options?.map(({ entityId, entityName, address, city }: any): any => (
                <Option key={entityId} value={entityId} data-testid={`search-hospital-option-${entityId}`}>
                  <div className="fsize_lg">{entityName}</div>
                  <div className="fsize_sm">
                    <div>{address}</div>
                    <div>
                      <span>{city}</span>
                    </div>
                  </div>
                </Option>
              ))}
          </Select>
        </Form.Item>
        {
          isHospLoading &&
          <Skeleton loading={isHospLoading} />
        }
        {
          selectedHospital &&
          <Form.Item label="Your selected hospital">
            <Card className="card-selected benef-card">
              <div className="tick-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                  <path d="M504.502 75.496c-9.997-9.998-26.205-9.998-36.204 0L161.594 382.203 43.702 264.311c-9.997-9.998-26.205-9.997-36.204 0-9.998 9.997-9.998 26.205 0 36.203l135.994 135.992c9.994 9.997 26.214 9.99 36.204 0L504.502 111.7c9.998-9.997 9.997-26.206 0-36.204z" />
                </svg>
              </div>
              <strong>{selectedHospital.providerName}</strong>
              <p>
                {selectedHospital.providerAddress},&nbsp;
                {selectedHospital.providerCity},&nbsp;
                {selectedHospital.providerState}, &nbsp;
                {selectedHospital.providerPinCode}
              </p>
              <div>
                <Text type="secondary">Rohini code:</Text>&nbsp;
              <span>{getDefaultStr(selectedHospital?.providerRohiniCode, true)}</span>
              </div>
              <div>
                <Text type="secondary">Provider email:</Text>&nbsp;
              <span>{getDefaultStr(selectedHospital.providerEmailAddress, true)}</span>
              </div>
              <div>
                <Text type="secondary">Configured payers:</Text>&nbsp;
                  <span>
                    {selectedHospital?.configuredPayers?.map(({ name }: any, index: number): any => (
                      <span className="mr-10" key={index}>
                        •&nbsp;{name}
                        {index < selectedHospital?.configuredPayers?.length - 1 && '  '}
                      </span>
                    ))}
                  </span>
              </div>
            </Card>
          </Form.Item>
        }
        <div className="text-right">
          <Button data-testid="hospital-selection-cancel" onClick={onClickCancel} className="mr-5">Cancel</Button>
          <Button data-testid="hospital-selection-proceed" type="primary" htmlType="submit" disabled={disabled}
            onClick={onProceed}>Proceed</Button>
        </div>
      </Form>
    </React.Fragment>
  );
};

export default ProviderSelectionComponent;
